import React, { lazy, Suspense } from 'react';
import 'aws-amplify/auth/enable-oauth-listener';
import '@business/assets/css/variables.css';
import '@business/assets/css/theme.css';
import '@business/assets/css/global.css';
import '@business/assets/css/swiper.css';
import '@business/assets/css/tree.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-number-input/style.css';
import { Amplify } from 'aws-amplify';
import { ComponentProvider } from '@business/workflows/ComponentHigherOrder';
import { Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ToastContainer } from 'react-toastify';
import { RouterInfo } from '@business/entities/base';
import MyProfile from '@business/workflows/ComponentViews/MyProfile/MyProfile';
import { FamilyTreeProvider } from '@business/workflows/ComponentViews/FamilyTree/useFamilyTreeContext';
import { AuthLayout } from './layouts/AuthLayout';
import amplifyconfig from './amplifyconfiguration.json';
import AdminLayout from './layouts/AdminLayout';
import { BaseAppLayout } from './layouts';

const SignUp = lazy(() => import('@pages/signup'));
const SignIn = lazy(() => import('@pages/sign-in'));
const ForgotPassword = lazy(
  () =>
    import('@business/workflows/ComponentViews/ForgotPassword/ForgotPassword')
);
const ResetPassword = lazy(
  () => import('@business/workflows/ComponentViews/ResetPassword/ResetPassword')
);
const SetPassword = lazy(
  () => import('@business/workflows/ComponentUI/PasswordReset/index')
);
const TwoFactorAuthentication = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/TwoFactorAuthentication/TwoFactorAuthentication'
    )
);

const PrivacyPolicy = lazy(
  () => import('@business/workflows/ComponentViews/PrivacyPolicy/PrivacyPolicy')
);
const TermsAndConditions = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/TermsAndConditions/TermsAndConditions'
    )
);

const Subscription = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Subscription/SubscriptionPlanList'
    )
);
const SelectedSubscriptionPlan = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Subscription/SelectedSubscriptionPlan'
    )
);
const PaymentSuccess = lazy(
  () => import('@business/workflows/ComponentViews/Subscription/PaymentSuccess')
);

const EditProfile = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/EditProfile')
);

const SubscriptionSettings = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/SubscriptionsSettings'
    )
);
const PaymentSettings = lazy(
  () => import('@business/workflows/ComponentViews/AccountSettings/Payments')
);
const SecurityPrivacySettings = lazy(
  () =>
    import('@business/workflows/ComponentViews/AccountSettings/SecurityPrivacy')
);
const NotificationSettings = lazy(
  () =>
    import('@business/workflows/ComponentViews/AccountSettings/Notification')
);
const HelpSettings = lazy(
  () => import('@business/workflows/ComponentViews/AccountSettings/Help')
);
const TakeAPicture = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/TakeAPicture')
);
const ManageSubscriptions = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/SubscriptionsSettings/ManageSubscriptions'
    )
);
const StorageSettings = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/AccountSettings/StorageContributorSettings/StorageContributorSettings'
    )
);
const Photos = lazy(
  () => import('@business/workflows/ComponentViews/Photos/Photos')
);

const PhotoCamera = lazy(
  () => import('@business/workflows/ComponentViews/MyProfile/TakeAPicture')
);
const MediaPostWrapper = lazy(
  () => import('@business/workflows/ComponentViews/Photos/MediaPostWrapper')
);

const Folder = lazy(
  () => import('@business/workflows/ComponentViews/Folder/Folder')
);
const FolderPhotos = lazy(
  () => import('@business/workflows/ComponentViews/Folder/FolderPhotos')
);
const FolderRecipes = lazy(
  () => import('@business/workflows/ComponentViews/Folder/FolderRecipes')
);
const People = lazy(
  () => import('@business/workflows/ComponentViews/People/People')
);
const PeopleDetail = lazy(
  () => import('@business/workflows/ComponentViews/People/PeopleDetail')
);
const MomentDetails = lazy(
  () => import('@business/workflows/ComponentViews/Moments/MomentDetails')
);
const Favorites = lazy(
  () => import('@business/workflows/ComponentViews/Favorites/Favorites')
);
const FavoritesList = lazy(
  () => import('@business/workflows/ComponentViews/Favorites/FavoritesViewAll')
);
const FamilyTree = lazy(
  () => import('@business/workflows/ComponentViews/FamilyTree/FamilyTree')
);
const FamilyTreeImport = lazy(
  () => import('@business/workflows/ComponentViews/FamilyTree/FamilyTreeImport')
);
const Memories = lazy(
  () => import('@business/workflows/ComponentViews/Memories/Memories')
);
const MemoryDetails = lazy(
  () => import('@business/workflows/ComponentViews/Memories/MemoryDetail')
);
const Recipes = lazy(
  () => import('@business/workflows/ComponentViews/Recipies/Recipies')
);
const RecipeDetails = lazy(
  () => import('@business/workflows/ComponentViews/Recipies/RecipeDetails')
);
const Sharing = lazy(
  () => import('@business/workflows/ComponentViews/Sharing/Sharing')
);
const NotificationsView = lazy(
  () => import('@business/workflows/ComponentViews/Notifications/Notifications')
);
const GroupDetailPage = lazy(
  () =>
    import('@business/workflows/ComponentViews/Sharing/components/GroupDetail')
);
const Storage = lazy(
  () => import('@business/workflows/ComponentViews/Storage/Storage')
);
const Trash = lazy(
  () => import('@business/workflows/ComponentViews/Trash/Trash')
);
const Tutorial = lazy(
  () => import('@business/workflows/ComponentViews/Tutorial/Tutorial')
);
const TrashViewAll = lazy(
  () => import('@business/workflows/ComponentViews/Trash/TrashViewAll')
);
const Diaries = lazy(
  () => import('@business/workflows/ComponentViews/Diary/Diaries')
);
const Admin = lazy(
  () => import('@business/workflows/ComponentViews/Admin/Admin')
);
const UserDirectory = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/UserManagement/UserDirectory'
    )
);
const UserSegmentation = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/UserManagement/UserSegmentation'
    )
);
const UserSegmentationUsers = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/UserManagement/UserSegmentationUsers'
    )
);
const AccountLevelAlerts = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/ContentModeration/AccountLevelAlerts'
    )
);
const AIModeration = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/ContentModeration/AIModeration'
    )
);
const ManualReview = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/ContentModeration/ManualReview'
    )
);
const ContentFlagging = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/ContentModeration/ContentFlagging'
    )
);
const SubscriptionDashboard = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/SubscriptionManagement/Dashboard'
    )
);
const TierManagement = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/SubscriptionManagement/Tier-management'
    )
);
const AdminSubscriptionList = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/SubscriptionManagement/SubscriptionList'
    )
);
const Analytics = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/SubscriptionManagement/Analytics'
    )
);
const AnalyticsOverview = lazy(
  () => import('@business/workflows/ComponentViews/Admin/Analytics/Overview')
);
const SalesAndRevenue = lazy(
  () =>
    import('@business/workflows/ComponentViews/Admin/Analytics/SalesAndRevenue')
);
const ProductPerformance = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/Analytics/ProductPerformance'
    )
);
const AdminUsers = lazy(
  () =>
    import('@business/workflows/ComponentViews/Admin/SystemAdmin/AdminUsers')
);
const SystemSettings = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/SystemAdmin/SystemSettings'
    )
);
const Integrations = lazy(
  () =>
    import('@business/workflows/ComponentViews/Admin/SystemAdmin/Integrations')
);
const DownloadManagement = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/Security/DownloadManagement'
    )
);
const StorageManagement = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/Security/StorageManagement'
    )
);
const AdminTutorials = lazy(
  () =>
    import('@business/workflows/ComponentViews/Admin/Tutorials/AdminTutorials')
);
const PaymentsAndBilling = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/Financial/PaymentsAndBilling'
    )
);
const FinancialAnalytics = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/Financial/FinancialAnalytics'
    )
);
const CustomerReports = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/CustomerAnalytics/CustomerReports'
    )
);
const CohortAnalysis = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/CustomerAnalytics/CohortAnalysis'
    )
);
const LifeTimeValue = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/CustomerAnalytics/LifeTimeValue'
    )
);
const EngagementMetrics = lazy(
  () =>
    import(
      '@business/workflows/ComponentViews/Admin/CustomerAnalytics/EngagementMetrices'
    )
);
const NotFound = lazy(() => import('@pages/not-found'));
const App = () => {
  Amplify.configure(amplifyconfig);

  const fallbackRender = (_error: any) => {
    return (
      <>
        <div />
      </>
    );
  };

  const convertComponent = (routerInfo: any) => {
    switch (routerInfo?.layout) {
      case 'error':
        return <>{routerInfo?.element}</>;
      case 'dashboard':
        return <>{routerInfo?.element}</>;
      case 'admin':
        return <AdminLayout>{routerInfo?.element}</AdminLayout>;
      case 'auth':
        return (
          <AuthLayout isSignUp={routerInfo?.path === '/signup'}>
            {routerInfo?.element}
          </AuthLayout>
        );
      case 'public':
        return <>{routerInfo?.element}</>;
      default:
        return <>{routerInfo?.element}</>;
    }
  };

  const pageRouters: RouterInfo[] = [
    {
      path: '/',
      element: <SignIn />,
      layout: 'dashboard'
    },
    {
      path: '/signup',
      element: <SignUp />,
      layout: 'auth'
    },
    {
      path: '/forgot-password',
      element: <ForgotPassword />,
      layout: 'auth'
    },
    {
      path: '/reset-password/:token',
      element: <ResetPassword />,
      layout: 'auth'
    },
    {
      path: '/two-factor-authentication',
      element: <TwoFactorAuthentication />,
      layout: 'auth'
    },
    {
      path: '/subscription',
      element: (
        <BaseAppLayout>
          <Subscription />
        </BaseAppLayout>
      ),
      layout: 'dashboard'
    },
    {
      path: '/selected-subscription',
      element: <SelectedSubscriptionPlan />,
      layout: 'dashboard'
    },
    {
      path: '/payment-success',
      element: <PaymentSuccess />,
      layout: 'dashboard'
    },
    {
      path: '/my-profile/edit',
      element: <EditProfile />,
      layout: ''
    },
    {
      path: '/settings/subscriptions',
      element: <SubscriptionSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/payments',
      element: <PaymentSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/security-privacy',
      element: <SecurityPrivacySettings />,
      layout: 'profile'
    },
    {
      path: '/settings/notification',
      element: <NotificationSettings />,
      layout: 'profile'
    },
    {
      path: '/settings/help',
      element: <HelpSettings />,
      layout: 'profile'
    },
    {
      path: '/my-profile',
      element: <MyProfile />,
      layout: 'profile'
    },
    {
      path: '/my-profile/camera',
      element: <TakeAPicture />,
      layout: 'profile'
    },
    {
      path: '/settings/subscriptions/manage',
      element: <ManageSubscriptions />,
      layout: 'profile'
    },
    {
      path: '/settings/storage',
      element: <StorageSettings />,
      layout: 'profile'
    },
    {
      path: '/photos',
      element: <Photos />,
      layout: 'profile'
    },
    {
      path: '/photos/media/:assetId',
      element: <MediaPostWrapper />,
      layout: 'profile'
    },
    {
      path: '/photos/camera',
      element: <PhotoCamera />,
      layout: 'profile'
    },
    {
      path: '/photo/folders',
      element: <Folder />,
      layout: 'profile'
    },
    {
      path: '/photo/folders/:folderName',
      element: <FolderPhotos />,
      layout: 'profile'
    },
    {
      path: '/recipes/folder/:folderId',
      element: <FolderRecipes />,
      layout: 'profile'
    },
    {
      path: '/people',
      element: <People />,
      layout: 'profile'
    },
    {
      path: '/people/family',
      element: <People type="family" />,
      layout: 'profile'
    },
    {
      path: '/people/friends',
      element: <People type="friend" />,
      layout: 'profile'
    },
    {
      path: '/people/:personId/:status?',
      element: <PeopleDetail />,
      layout: 'profile'
    },
    {
      path: '/people/:personId/moments/:momentId',
      element: <MomentDetails />,
      layout: 'profile'
    },
    {
      path: '/favorites',
      element: <Favorites />,
      layout: 'profile'
    },
    {
      path: '/favorites/view',
      element: <FavoritesList />,
      layout: 'profile'
    },
    {
      path: '/family-tree',
      element: (
        <FamilyTreeProvider>
          <FamilyTree />
        </FamilyTreeProvider>
      ),
      layout: 'profile'
    },
    {
      path: '/shared-family-tree',
      element: (
        <FamilyTreeProvider>
          <FamilyTree isFromSharing={true} />
        </FamilyTreeProvider>
      ),
      layout: 'profile'
    },
    {
      path: '/family-tree/import',
      element: <FamilyTreeImport />,
      layout: 'profile'
    },
    {
      path: '/memories',
      element: <Memories />,
      layout: 'profile'
    },
    {
      path: '/memories/:memoryId',
      element: <MemoryDetails />,
      layout: 'profile'
    },
    {
      path: '/recipes',
      element: <Recipes />,
      layout: 'profile'
    },
    {
      path: '/recipes/:recipeId',
      element: <RecipeDetails />,
      layout: 'profile'
    },
    {
      path: '/sharing',
      element: <Sharing />,
      layout: 'profile'
    },
    {
      path: '/notifications',
      element: <NotificationsView />,
      layout: 'profile'
    },
    {
      path: '/sharing/groups/:groupId',
      element: <GroupDetailPage />,
      layout: 'profile'
    },
    {
      path: '/storage',
      element: <Storage />,
      layout: 'profile'
    },
    {
      path: '/trash',
      element: <Trash />,
      layout: 'profile'
    },
    {
      path: '/moments/:momentId/diaries',
      element: <Diaries />,
      layout: 'profile'
    },
    {
      path: '/tutorial',
      element: <Tutorial />,
      layout: 'profile'
    },
    {
      path: '/trash/view',
      element: <TrashViewAll />,
      layout: 'profile'
    },
    {
      path: '/set-password/request/:userId',
      element: <SetPassword />,
      layout: 'auth'
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicy />,
      layout: 'auth'
    },
    {
      path: '/terms-and-conditions',
      element: <TermsAndConditions />,
      layout: 'auth'
    },
    {
      path: '/admin',
      element: <Admin />,
      layout: 'admin'
    },
    {
      path: '/admin/user-management/user-directory',
      element: <UserDirectory />,
      layout: 'admin'
    },
    {
      path: '/admin/user-management/user-segmentation',
      element: <UserSegmentation />,
      layout: 'admin'
    },
    {
      path: '/admin/user-management/user-segmentation/:segmentKey',
      element: <UserSegmentationUsers />,
      layout: 'admin'
    },
    {
      path: '/admin/content-moderation/ai-moderation',
      element: <AIModeration />,
      layout: 'admin'
    },
    {
      path: '/admin/content-moderation/account-level-alerts',
      element: <AccountLevelAlerts />,
      layout: 'admin'
    },
    {
      path: '/admin/content-moderation/manual-review',
      element: <ManualReview />,
      layout: 'admin'
    },
    {
      path: '/admin/content-moderation/content-flagging',
      element: <ContentFlagging />,
      layout: 'admin'
    },
    {
      path: '/admin/subscription-management/subscription-dashboard',
      element: <SubscriptionDashboard />,
      layout: 'admin'
    },
    {
      path: '/admin/subscription-management/subscription-list',
      element: <AdminSubscriptionList />,
      layout: 'admin'
    },
    {
      path: '/admin/subscription-management/tier-management',
      element: <TierManagement />,
      layout: 'admin'
    },
    {
      path: '/admin/subscription-management/analytics',
      element: <Analytics />,
      layout: 'admin'
    },
    {
      path: '/admin/analytics/overview',
      element: <AnalyticsOverview />,
      layout: 'admin'
    },
    {
      path: '/admin/analytics/sales-revenue',
      element: <SalesAndRevenue />,
      layout: 'admin'
    },
    {
      path: '/admin/analytics/product-performance',
      element: <ProductPerformance />,
      layout: 'admin'
    },
    {
      path: '/admin/system-admin/admin-users',
      element: <AdminUsers />,
      layout: 'admin'
    },
    {
      path: '/admin/system-admin/system-settings',
      element: <SystemSettings />,
      layout: 'admin'
    },
    {
      path: '/admin/system-admin/integrations',
      element: <Integrations />,
      layout: 'admin'
    },
    {
      path: '/admin/security/storage-management',
      element: <StorageManagement />,
      layout: 'admin'
    },
    {
      path: '/admin/tutorials',
      element: <AdminTutorials />,
      layout: 'admin'
    },
    {
      path: '/admin/security/download-management',
      element: <DownloadManagement />,
      layout: 'admin'
    },
    {
      path: '/admin/financial/payments-billing',
      element: <PaymentsAndBilling />,
      layout: 'admin'
    },
    {
      path: '/admin/financial/analytics',
      element: <FinancialAnalytics />,
      layout: 'admin'
    },
    {
      path: '/admin/customer-analytics/customer-reports',
      element: <CustomerReports />,
      layout: 'admin'
    },
    {
      path: '/admin/customer-analytics/cohort-analysis',
      element: <CohortAnalysis />,
      layout: 'admin'
    },
    {
      path: '/admin/customer-analytics/lifetime-value',
      element: <LifeTimeValue />,
      layout: 'admin'
    },
    {
      path: '/admin/customer-analytics/engagement-metrics',
      element: <EngagementMetrics />,
      layout: 'admin'
    },
    {
      path: '/NotFound',
      element: <NotFound />,
      layout: 'public'
    }
  ];

  return (
    <ComponentProvider>
      <ErrorBoundary
        fallbackRender={fallbackRender}
        onReset={() => (location.href = '/')}>
        <Suspense fallback={<></>}>
          <Routes>
            {pageRouters.map(routeObj => (
              <Route
                path={routeObj?.path}
                element={convertComponent(routeObj)}
                key={routeObj?.path}
              />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ComponentProvider>
  );
};

export default App;
